/**
 * Main Menu Component
 *
 * Handles the main toolbar with puzzle operations, cell operations, and view settings.
 * Migrated from the legacy Menu constructor function in olaszmodra.js.
 */

import type {
    ButtonState,
    MainMenuContext,
    HistoryContext,
    CellContext,
} from '../types/menu.types';

/**
 * Configuration for MainMenu
 */
export interface MainMenuConfig {
    /** Menu context with state accessors */
    context: MainMenuContext;
    /** History context for undo/redo */
    history: HistoryContext;
    /** Cell context for button states */
    cell: CellContext;

    // Puzzle operations
    /** Callback for save action */
    onSave?: () => void;
    /** Callback for undo action */
    onUndo?: () => void;
    /** Callback for redo action */
    onRedo?: () => void;
    /** Callback for search action */
    onSearch?: () => void;
    /** Callback for verify action */
    onVerify?: () => void;
    /** Callback for unverify action */
    onUnverify?: () => void;

    // Cell operations
    /** Callback for set colored action */
    onSetColored?: (element: HTMLElement) => void;
    /** Callback for set bridge action */
    onSetBridge?: (element: HTMLElement) => void;
    /** Callback for set empty action */
    onSetEmpty?: (element: HTMLElement) => void;
    /** Callback for set image action */
    onSetImage?: (element: HTMLElement) => void;
    /** Callback for set black action */
    onSetBlack?: (element: HTMLElement) => void;
    /** Callback for set vertical line action */
    onSetVerticalLine?: (element: HTMLElement) => void;
    /** Callback for set horizontal line action */
    onSetHorizontalLine?: (element: HTMLElement) => void;
    /** Callback for set known action */
    onSetKnown?: (element: HTMLElement) => void;

    // Editor operations
    /** Callback for toggle sync editing action */
    onToggleSync?: (element: HTMLElement) => void;
    /** Callback for toggle direction action */
    onToggleDirection?: (element: HTMLElement) => void;

    // View operations
    /** Callback for toggle known marking action */
    onToggleKnownMarking?: (element: HTMLElement) => void;
    /** Callback for toggle clue marking action */
    onToggleClueMarking?: (element: HTMLElement) => void;
    /** Callback for toggle mask marking action */
    onToggleMaskMarking?: (element: HTMLElement) => void;

    // Graphics operations
    /** Callback for change mask action */
    onChangeMask?: (element: HTMLElement) => void;
    /** Callback for change color scheme action */
    onChangeColorScheme?: (element: HTMLElement) => void;

    // Other operations
    /** Callback for show info action */
    onShowInfo?: (element: HTMLElement) => void;
}

/**
 * Helper to create anchor attributes based on state
 */
function getAnchorAttrs(state: ButtonState): string {
    if (state === 'active') return ' class="aktiv"';
    if (state === 'inactive') return ' class="inaktiv"';
    return '';
}

/**
 * Main Menu Component
 *
 * Generates HTML for the main toolbar sections.
 */
export class MainMenu {
    private readonly _config: MainMenuConfig;

    constructor(config: MainMenuConfig) {
        this._config = config;
    }

    // ============================================================================
    // Puzzle Operations (Save, Undo, Redo, Search, Verify)
    // ============================================================================

    /**
     * Generate HTML for puzzle operation buttons
     */
    getPuzzleButtons(): string {
        let html = '';
        const imgPath = this._config.context.getImagePath();

        // Save button
        const isSaved = this._config.context.getIsSaved();
        const saveIcon = isSaved ? 'gomb_mentes.png' : 'gomb_mentes_x.png';
        html += `<a href="javascript:;" data-action="save" desc="Mentés (Ctrl+S)"><img src="${imgPath}${saveIcon}"></a>`;

        // Undo button
        const undoTooltip = this._config.history.hasUndo()
            ? this._config.history.getUndoTooltip()
            : 'Visszavonás';
        const undoClass = this._config.history.hasUndo()
            ? ''
            : ' class="inaktiv"';
        html += `<a href="javascript:;" data-action="undo" desc="${undoTooltip}"${undoClass}><img src="${imgPath}gomb_visszavonas.png"></a>`;

        // Redo button
        const redoTooltip = this._config.history.hasRedo()
            ? this._config.history.getRedoTooltip()
            : 'Ismét';
        const redoClass = this._config.history.hasRedo()
            ? ''
            : ' class="inaktiv"';
        html += `<a href="javascript:;" data-action="redo" desc="${redoTooltip}"${redoClass}><img src="${imgPath}gomb_ismet.png"></a>`;

        html += '</span><span class="blokk">';

        // Search button
        const isBuilderMode = this._config.context.getIsBuilderMode();
        const searchClass = isBuilderMode ? '' : ' class="inaktiv"';
        html += `<a href="javascript:;" data-action="search" desc="Kifejezés és meghatározás keresés"${searchClass}><img src="${imgPath}gomb_keres.png"></a>`;

        // Verify button
        const isVerified = this._config.context.getIsVerified();
        const verifyAction = isVerified ? 'unverify' : 'verify';
        const verifyClass = isVerified ? ' class="aktiv"' : '';
        html += `<a href="javascript:;" data-action="${verifyAction}" desc="Ellenőrzés"${verifyClass}><img src="${imgPath}gomb_ellenorzes.png"></a>`;

        return html;
    }

    // ============================================================================
    // Cell Operations (Colored, Bridge, Empty, Image, Black, Lines, Known)
    // ============================================================================

    /**
     * Generate HTML for cell operation buttons
     */
    getCellButtons(): string {
        let html = '';
        const imgPath = this._config.context.getImagePath();

        // Colored button
        const coloredState = this.getColoredButtonState();
        const coloredBase64 = this._config.context.getColoredBase64();
        html += `<a href="javascript:;" data-action="set-colored" desc="Megfejtés betűje (Ctrl+M)"${getAnchorAttrs(
            coloredState,
        )}><img src="${coloredBase64}"></a>`;

        // Bridge button
        const bridgeState = this.getBridgeButtonState();
        const bridgeBase64 = this._config.context.getBridgeBase64();
        html += `<a href="javascript:;" data-action="set-bridge" desc="Híd betűje (Ctrl+H)"${getAnchorAttrs(
            bridgeState,
        )}><img src="${bridgeBase64}"></a>`;

        // Empty button
        const emptyState = this.getEmptyButtonState();
        html += `<a href="javascript:;" data-action="set-empty" desc="Üresen marad (Ctrl+A)"${getAnchorAttrs(
            emptyState,
        )}><img src="${imgPath}gomb_ures.png"></a>`;

        // Image button
        const imageState = this.getImageButtonState();
        html += `<a href="javascript:;" data-action="set-image" desc="Ábra helye (Ctrl+A)"${getAnchorAttrs(
            imageState,
        )}><img src="${imgPath}gomb_abra.png"></a>`;

        // Black button
        const blackState = this.getBlackButtonState();
        html += `<a href="javascript:;" data-action="set-black" desc="Fekete négyzet (Ctrl+N)"${getAnchorAttrs(
            blackState,
        )}><img src="${imgPath}gomb_fekete.png"></a>`;

        // Vertical line button
        const vLineState = this.getVerticalLineButtonState();
        html += `<a href="javascript:;" data-action="set-vline" desc="Vízszintes vonal (Ctrl+V)"${getAnchorAttrs(
            vLineState,
        )}><img src="${imgPath}gomb_vvonal.png"></a>`;

        // Horizontal line button
        const hLineState = this.getHorizontalLineButtonState();
        html += `<a href="javascript:;" data-action="set-hline" desc="Függőleges vonal (Ctrl+F)"${getAnchorAttrs(
            hLineState,
        )}><img src="${imgPath}gomb_fvonal.png"></a>`;

        // Known button
        const knownState = this.getKnownButtonState();
        html += `<a href="javascript:;" data-action="set-known" desc="Ismert mező (Ctrl+I)"${getAnchorAttrs(
            knownState,
        )}><img src="${imgPath}gomb_ismert.png"></a>`;

        return html;
    }

    // ============================================================================
    // Editor Operations (Sync, Direction)
    // ============================================================================

    /**
     * Generate HTML for editor operation buttons
     */
    getEditorButtons(): string {
        let html = '';
        const imgPath = this._config.context.getImagePath();

        // Sync editing button
        const syncState = this.getSyncEditingButtonState();
        html += `<a href="javascript:;" data-action="toggle-sync" desc="Szinkronizált szerkesztés (Ctrl+E)"${getAnchorAttrs(
            syncState,
        )}><img src="${imgPath}gomb_szinkron.png"></a>`;

        // Direction button
        const dirState = this.getDirectionButtonState();
        html += `<a href="javascript:;" data-action="toggle-direction" desc="Függőleges kijelölési irány (Space)"${getAnchorAttrs(
            dirState,
        )}><img src="${imgPath}gomb_mezoirany.png"></a>`;

        return html;
    }

    // ============================================================================
    // View Operations (Known marking, Clue marking, Mask marking)
    // ============================================================================

    /**
     * Generate HTML for view operation buttons
     */
    getViewButtons(): string {
        let html = '';
        const imgPath = this._config.context.getImagePath();

        // Known marking button
        const knownMarkingState = this.getKnownMarkingButtonState();
        html += `<a href="javascript:;" data-action="toggle-known-marking" desc="Ismert mezők"${getAnchorAttrs(
            knownMarkingState,
        )}><img src="${imgPath}gomb_ismertjeloles.png"></a>`;

        // Clue marking button
        const clueMarkingState = this.getClueMarkingButtonState();
        html += `<a href="javascript:;" data-action="toggle-clue-marking" desc="Meghatározások helyei"${getAnchorAttrs(
            clueMarkingState,
        )}><img src="${imgPath}gomb_jeloles.png"></a>`;

        // Mask marking button (always shown, state based on allowAbra and hasGraphics)
        const maskMarkingState = this.getMaskMarkingButtonState();
        html += `<a href="javascript:;" data-action="toggle-mask-marking" desc="Maszk megjelenítése"${getAnchorAttrs(
            maskMarkingState,
        )}><img src="${imgPath}gomb_setmaszk.png"></a>`;

        return html;
    }

    // ============================================================================
    // Graphics Operations (Mask upload, Color scheme)
    // ============================================================================

    /**
     * Generate HTML for graphics operation buttons
     */
    getGraphicsButtons(): string {
        let html = '';
        const imgPath = this._config.context.getImagePath();
        const isBuilderMode = this._config.context.getIsBuilderMode();
        const allowAbra = this._config.context.getAllowImages();

        // Mask upload button (based on settings, not builder mode)
        const maskClass = allowAbra ? '' : ' class="inaktiv"';
        html += `<a href="javascript:;" data-action="change-mask" desc="Maszk feltöltése"${maskClass}><img src="${imgPath}gomb_changemaszk.png"></a>`;

        // Color scheme button (builder mode only)
        const colorClass = isBuilderMode ? '' : ' class="inaktiv"';
        html += `<a href="javascript:;" data-action="change-color-scheme" desc="Színséma kiválasztása"${colorClass}><img src="${imgPath}gomb_szinsema.png"></a>`;

        return html;
    }

    // ============================================================================
    // Other Operations (Info)
    // ============================================================================

    /**
     * Generate HTML for other operation buttons
     */
    getOtherButtons(): string {
        const imgPath = this._config.context.getImagePath();

        // Info button
        return `<a href="javascript:;" data-action="show-info" desc="Információ"><img src="${imgPath}gomb_info.png"></a>`;
    }

    // ============================================================================
    // Button State Helpers
    // ============================================================================

    private getColoredButtonState(): ButtonState {
        // Legacy: allowMegfejtes() check
        if (!this._config.context.getAllowColored()) return 'inactive';
        if (this._config.cell.isColored()) return 'active';
        return 'normal';
    }

    private getBridgeButtonState(): ButtonState {
        // Legacy: allowHid() check
        if (!this._config.context.getAllowBridge()) return 'inactive';
        if (this._config.cell.isBridge()) return 'active';
        return 'normal';
    }

    private getEmptyButtonState(): ButtonState {
        // Legacy: allowUres() && !isIsmert()
        if (!this._config.context.getAllowEmpty()) return 'inactive';
        if (!this._config.cell.canBeEmpty()) return 'inactive';
        if (this._config.cell.isEmpty()) return 'active';
        return 'normal';
    }

    private getImageButtonState(): ButtonState {
        // Legacy: allowAbra() && !isIsmert() && !lines
        if (!this._config.context.getAllowImages()) return 'inactive';
        if (!this._config.cell.canBeImage()) return 'inactive';
        if (this._config.cell.isImage()) return 'active';
        return 'normal';
    }

    private getBlackButtonState(): ButtonState {
        // Legacy: allowFekete() && !isIsmert() && !lines
        if (!this._config.context.getAllowBlack()) return 'inactive';
        if (!this._config.cell.canBeBlack()) return 'inactive';
        if (this._config.cell.isBlack()) return 'active';
        return 'normal';
    }

    private getVerticalLineButtonState(): ButtonState {
        if (!this._config.cell.canToggleVerticalLine()) return 'inactive';
        if (this._config.cell.hasVerticalLine()) return 'active';
        return 'normal';
    }

    private getHorizontalLineButtonState(): ButtonState {
        if (!this._config.cell.canToggleHorizontalLine()) return 'inactive';
        if (this._config.cell.hasHorizontalLine()) return 'active';
        return 'normal';
    }

    private getKnownButtonState(): ButtonState {
        // Legacy: regex.test(getErtek()) && g_epitoMod
        // canBeKnown checks for valid character + builder mode
        if (!this._config.cell.canBeKnown()) {
            // Special case: show as "inaktiv aktiv" if can't change but IS known
            if (this._config.cell.isKnown()) return 'active'; // Will also be inactive via canBeKnown
            return 'inactive';
        }
        if (this._config.cell.isKnown()) return 'active';
        return 'normal';
    }

    private getSyncEditingButtonState(): ButtonState {
        // Legacy: countHalozat() > 1 check for multiple grids
        if (!this._config.context.getHasMultipleGrids()) return 'inactive';
        if (this._config.context.getSyncEditing()) return 'active';
        return 'normal';
    }

    private getDirectionButtonState(): ButtonState {
        if (!this._config.context.getIsHorizontal()) return 'active';
        return 'normal';
    }

    private getKnownMarkingButtonState(): ButtonState {
        if (this._config.context.getKnownMarking()) return 'active';
        return 'normal';
    }

    private getClueMarkingButtonState(): ButtonState {
        // Legacy: only enabled in builder mode (g_epitoMod)
        if (!this._config.context.getIsBuilderMode()) return 'inactive';
        if (this._config.context.getClueMarking()) return 'active';
        return 'normal';
    }

    private getMaskMarkingButtonState(): ButtonState {
        // Mask button requires both allowAbra and hasGraphics to be enabled
        const allowAbra = this._config.context.getAllowImages();
        const hasGraphics = this._config.context.getHasGraphics();
        if (!allowAbra || !hasGraphics) return 'inactive';
        if (this._config.context.getMaskMarking()) return 'active';
        return 'normal';
    }

    // ============================================================================
    // Full Menu Render
    // ============================================================================

    /**
     * Render the complete menu HTML
     * Note: getPuzzleButtons() includes an internal </span><span class="blokk"> split
     * so we just close it with </span> like the legacy toString() does.
     */
    render(): string {
        let html = '';
        html += '<span class="blokk">' + this.getPuzzleButtons() + '</span>';
        html += '<span class="blokk">' + this.getCellButtons() + '</span>';
        html += '<span class="blokk">' + this.getEditorButtons() + '</span>';
        html += '<span class="blokk">' + this.getViewButtons() + '</span>';
        html += '<span class="blokk">' + this.getGraphicsButtons() + '</span>';
        html += '<span class="blokk">' + this.getOtherButtons() + '</span>';
        return html;
    }

    /**
     * Convert to string (for legacy compatibility).
     * Called when the legacy code does `innerHTML = g_foMenu`.
     */
    toString(): string {
        return this.render();
    }

    /**
     * Update the menu in the DOM
     */
    update(containerId = 'menusor'): void {
        const container = document.getElementById(containerId);
        if (container) {
            container.innerHTML = this.render();
        }
    }

    /**
     * Attach event listeners to the menu container using event delegation.
     * Should be called once after initial render.
     */
    attachEvents(container: HTMLElement): void {
        container.addEventListener('click', (e) => {
            const anchor = (e.target as HTMLElement).closest('a[data-action]');
            if (!anchor) return;

            if (anchor.classList.contains('inaktiv')) {
                e.preventDefault();
                return;
            }

            const action = anchor.getAttribute('data-action');
            if (!action) return;

            e.preventDefault();
            this.handleAction(action);
        });
    }

    /**
     * Handle a menu action by name.
     */
    private handleAction(action: string): void {
        const currentElement = this._config.context.getCurrentElement();

        // Actions that don't require currentElement
        const simpleActions: Record<string, (() => void) | undefined> = {
            save: this._config.onSave,
            undo: this._config.onUndo,
            redo: this._config.onRedo,
            search: this._config.onSearch,
            verify: this._config.onVerify,
            unverify: this._config.onUnverify,
        };

        const simpleHandler = simpleActions[action];
        if (simpleHandler) {
            simpleHandler();
            return;
        }

        // View toggle actions - use currentElement for focus, but work without it
        const viewActions: Record<
            string,
            ((el: HTMLElement) => void) | undefined
        > = {
            'toggle-known-marking': this._config.onToggleKnownMarking,
            'toggle-clue-marking': this._config.onToggleClueMarking,
            'toggle-mask-marking': this._config.onToggleMaskMarking,
        };

        const viewHandler = viewActions[action];
        if (viewHandler) {
            // Use currentElement if available, otherwise use document.body as fallback
            viewHandler(currentElement || document.body);
            return;
        }

        // Actions that require currentElement
        if (!currentElement) return;

        const elementActions: Record<
            string,
            ((el: HTMLElement) => void) | undefined
        > = {
            'set-colored': this._config.onSetColored,
            'set-bridge': this._config.onSetBridge,
            'set-empty': this._config.onSetEmpty,
            'set-image': this._config.onSetImage,
            'set-black': this._config.onSetBlack,
            'set-vline': this._config.onSetVerticalLine,
            'set-hline': this._config.onSetHorizontalLine,
            'set-known': this._config.onSetKnown,
            'toggle-sync': this._config.onToggleSync,
            'toggle-direction': this._config.onToggleDirection,
            'change-mask': this._config.onChangeMask,
            'change-color-scheme': this._config.onChangeColorScheme,
            'show-info': this._config.onShowInfo,
        };

        const elementHandler = elementActions[action];
        if (elementHandler) {
            elementHandler(currentElement);
        }
    }
}
